import moment from "moment";
import { gateReportePresentismoGet } from "services/services-mia/miakey";

function formatSignedDuration(seconds) {
    if (seconds == null || !Number.isFinite(Number(seconds))) return "N/A";
    const n = Number(seconds);
    const sign = n >= 0 ? "+" : "-";
    const abs = Math.abs(Math.round(n));
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const sec = abs % 60;
    const pad = (x) => String(x).padStart(2, "0");
    return `${sign} ${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function formatEventoFechaHora(fh) {
    if (!fh) return "";
    const m = moment(fh);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : String(fh);
}

function formatEventoHora(fh) {
    if (!fh) return "";
    const m = moment(fh);
    return m.isValid() ? m.format("HH:mm") : String(fh);
}

/** Misma regla que antes: fecha+hora si cruza día o madrugada; si no, solo HH:mm */
function formatSalidaPlanCell(entradaPlanTs, salidaPlanTs) {
    if (!salidaPlanTs) return "";
    const ent = moment(entradaPlanTs);
    const sal = moment(salidaPlanTs);
    if (!ent.isValid() || !sal.isValid()) return formatEventoFechaHora(salidaPlanTs);
    const entDay = ent.format("YYYY-MM-DD");
    const salDay = sal.format("YYYY-MM-DD");
    const salMin = sal.hour() * 60 + sal.minute();
    const isMadrugada = salMin >= 0 && salMin < 360;
    if (salDay !== entDay || isMadrugada) {
        return sal.format("DD/MM/YYYY HH:mm");
    }
    return sal.format("HH:mm");
}

function normalizeSpRows(raw) {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.rows)) return raw.rows;
    if (Array.isArray(raw.data)) return raw.data;
    if (typeof raw === "object") return [raw];
    return [];
}

function mapSpRowToGrid(row, index) {
    const {
        fechaBase,
        gateLugarId,
        gateHorarioId,
        gateLugarNombre,
        gateHorarioNombre,
        diaSemana,
        entradaPlanTs,
        salidaPlanTs,
        gateESHistoEntradaFH,
        gateESHistoSalidaFH,
        desvioEntradaSeg,
        desvioSalidaSeg,
    } = row;

    return {
        id: `${fechaBase}-${gateLugarId}-${gateHorarioId}-${index}`,
        fecha: fechaBase
            ? moment(fechaBase).format("DD/MM/YYYY")
            : "",
        lugar: gateLugarNombre ?? "",
        horario: gateHorarioNombre ?? "",
        diaSemana: diaSemana ?? "",
        entrada: entradaPlanTs
            ? moment(entradaPlanTs).format("HH:mm")
            : "",
        eventoEntrada: gateESHistoEntradaFH
            ? formatEventoHora(gateESHistoEntradaFH)
            : "Sin registro",
        desvioEntrada:
            gateESHistoEntradaFH != null
                ? formatSignedDuration(desvioEntradaSeg)
                : "N/A",
        salida: formatSalidaPlanCell(entradaPlanTs, salidaPlanTs),
        eventoSalida: gateESHistoSalidaFH
            ? formatSalidaPlanCell(entradaPlanTs, gateESHistoSalidaFH)
            : "Sin registro",
        desvioSalida:
            gateESHistoSalidaFH != null
                ? formatSignedDuration(desvioSalidaSeg)
                : "N/A",
    };
}

/**
 * Carga el reporte vía GateReportePresentismoGet.
 * @returns {Promise<{ rows: object[], error: string|null }>}
 */
export async function loadReportePresentismo({
    credentials,
    merchantServicioUsuarioId,
    usuarioId,
    fechaDesde,
    fechaHasta,
}) {
    if (usuarioId == null) {
        return { rows: [], error: "El usuario no tiene identificador (usuarioId)." };
    }

    const res = await gateReportePresentismoGet(
        {
            merchantServicioUsuarioId,
            usuarioId,
            fechaDesde,
            fechaHasta,
            page: null,
            pageSize: null,
            debug: 0,
        },
        credentials
    );

    const status = res?.status;
    if (status && status.code !== 1) {
        return {
            rows: [],
            error: status.errmsg || "No se pudo obtener el reporte de presentismo.",
        };
    }

    const list = normalizeSpRows(res?.result);
    const rows = list.map((r, i) => mapSpRowToGrid(r, i));

    return { rows, error: null };
}

export const reportePresentismoTitles = [
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "lugar", headerName: "Lugar", flex: 1, minWidth: 120 },
    { field: "horario", headerName: "Horario", width: 120 },
    { field: "diaSemana", headerName: "Día semana", width: 110 },
    { field: "entrada", headerName: "Entrada", width: 150 },
    { field: "eventoEntrada", headerName: "Evento entrada", width: 140 },
    { field: "desvioEntrada", headerName: "Desvío entrada", width: 125 },
    { field: "salida", headerName: "Salida", width: 150 },
    { field: "eventoSalida", headerName: "Evento salida", width: 140 },
    { field: "desvioSalida", headerName: "Desvío salida", width: 125 },
];
